import { useEffect, useMemo, type ReactNode } from 'react'

import { API_BASE } from '@/api/client'
import { useAuth } from '@/auth/AuthProvider'
import { CopyrightFooter } from '@/components/CopyrightFooter'
import { Badge, Button, cn } from '@/components/ui'
import {
  clearLastMatter,
  forgetLastMatterUnlessOwnedBy,
  readLastMatter,
  writeLastMatter,
  type LastMatter,
} from '@/lastMatter'
import { clearRunDefaults } from '@/runDefaults'
import { EXTERNAL_LINKS, tabForPath, visibleTabs, type Router, type TabKey } from '@/routes'
import { parseCasePath } from '@/screens/Case/casePath'
import { buildLabel, buildTitle } from '@/version'

export interface AppShellProps {
  router: Router
  activeTab: TabKey | null
  children: ReactNode
}

/**
 * The matter a path names, when it is a Case or Workbench path.
 *
 * Only those two tabs take a matter in the URL. `parseCasePath` is the one
 * place that knows how that path is split and decoded, so this does not
 * re-split it.
 */
function matterOnPath(path: string): LastMatter | null {
  const key = tabForPath(path)?.key
  if (key !== 'case' && key !== 'workbench') return null
  const { matterId, runId } = parseCasePath(path)
  return matterId ? { matterId, runId } : null
}

/** `/case` or `/workbench`, narrowed to the matter (and run) a person last had open. */
function pathWithMatter(base: string, matter: LastMatter): string {
  const matterPart = `/${encodeURIComponent(matter.matterId)}`
  return `${base}${matterPart}${matter.runId ? `/${encodeURIComponent(matter.runId)}` : ''}`
}

/**
 * The frame every screen sits in: the six-tab header from `design/Gateway.dc.html`,
 * then a max-1560px content column.
 *
 * Two departures from the mockup, both deliberate:
 *
 * - The mockup's role segment control is a *demo* device for flipping between
 *   analyst/bd/admin views. Here the role comes from the session, so the
 *   header states it and offers sign-out instead of pretending it is editable.
 * - The mockup's "tick 04:32 ago" chip reports the collector, which is P9.2.
 *   It is absent rather than faked.
 */
export function AppShell({ router, activeTab, children }: AppShellProps) {
  const { user, me, role, isAdmin, signOut, isSigningOut } = useAuth()
  const tabs = visibleTabs(isAdmin)
  const identity = user?.email ?? me?.actor ?? 'signed in'

  // Who the remembered matter belongs to. The user id rather than the email
  // shown in the header: it is what stays put if an address is edited.
  const owner = user?.id ?? me?.actor ?? null

  const here = useMemo(() => matterOnPath(router.path), [router.path])

  // First, so that a record left behind by somebody else is gone before
  // anything below could record over or read it.
  useEffect(() => {
    if (owner) forgetLastMatterUnlessOwnedBy(owner)
  }, [owner])

  useEffect(() => {
    if (owner && here) writeLastMatter(owner, here)
  }, [owner, here])

  // The matter on screen wins over the stored one, so the tab for the matter
  // you are looking at never lags a render behind the effect above. Read on
  // every render rather than memoised: it is one tiny `sessionStorage` read,
  // and it cannot go stale.
  const lastMatter = here ?? readLastMatter(owner)

  // Case and Workbench go back to the remembered matter; with nothing
  // remembered they keep their bare path and the screen's own empty state.
  const hrefFor = (tab: { key: TabKey; path: string }): string =>
    lastMatter && (tab.key === 'case' || tab.key === 'workbench')
      ? router.href(pathWithMatter(tab.path, lastMatter))
      : router.href(tab.path)

  return (
    <div className="flex min-h-full flex-col bg-ground">
      {/* 14px/28px padding, 28px gap, 22px between links, hairline rule and no
          fill of its own — the mockup's header measurements exactly. */}
      <header className="flex flex-wrap items-baseline gap-x-7 gap-y-2 border-b border-divider px-7 py-3.5">
        <div className="text-[18px] font-semibold tracking-[-0.01em]">TM-Traverse</div>

        <nav aria-label="Screens" className="flex flex-wrap items-baseline gap-x-[22px] gap-y-1">
          {tabs.map((tab) => (
            <a
              key={tab.key}
              href={hrefFor(tab)}
              aria-current={activeTab === tab.key ? 'page' : undefined}
              className={cn('gw-navlink', 'no-underline')}
            >
              {tab.label}
            </a>
          ))}

          {/* Real pages this application does not itself render — see
              `EXTERNAL_LINKS`. Visually the same weight as a tab, because to
              the person using it the law corpus is another screen; the new
              tab and the title attribute are what say it leaves the app. */}
          {EXTERNAL_LINKS.map((link) => (
            <a
              key={link.label}
              href={`${API_BASE}${link.path}`}
              target="_blank"
              rel="noopener noreferrer"
              title={link.hint}
              className={cn('gw-navlink', 'no-underline')}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3.5">
          {/* Which bundle is this? Twice a deploy was debugged from the wrong
              end because the page and the commit on the box were different
              things and nothing said so. Muted and out of the way, but never
              hidden — including when it reads `unknown`. */}
          <span
            className="hidden font-mono text-micro text-muted lg:inline"
            title={buildTitle()}
          >
            {buildLabel()}
          </span>
          <span className="hidden text-meta text-muted sm:inline" title={identity}>
            {identity}
          </span>
          {role ? <Badge tone="accent">{role}</Badge> : null}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              // Before the request, not after it: a failed logout still ends
              // the session client-side (see `AuthProvider`), and the next
              // person at this tab must not be offered this one's matter.
              clearLastMatter()
              clearRunDefaults()
              void signOut()
            }}
            loading={isSigningOut}
            loadingLabel="Signing out…"
          >
            Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-shell flex-1 px-7 pb-14 pt-6">{children}</main>

      <CopyrightFooter className="border-t border-divider" />
    </div>
  )
}
